import { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Drawer,
  Tooltip,
  CircularProgress,
  InputAdornment,
  MenuItem,
  Alert,
  Snackbar,
  ToggleButtonGroup,
  ToggleButton,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ProvisionIcon from '@mui/icons-material/AddCard';
import TrialIcon from '@mui/icons-material/EventRepeat';
import EditIcon from '@mui/icons-material/Edit';
import SyncIcon from '@mui/icons-material/Sync';
import CloseIcon from '@mui/icons-material/Close';
import billingService, {
  type AdminTenantSubscription,
  type Plan,
  type Subscription,
} from '../../services/billingService';
import { getApiErrorInfo } from '../../services/api';
import {
  SUBSCRIPTION_STATUS_CONFIG,
  NO_SUBSCRIPTION_CONFIG,
  subscriptionChipSx,
} from '../../utils/subscriptionStatus';

function formatDate(iso?: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function formatDateTime(iso?: string | null): string {
  if (!iso) return 'unknown';
  return new Date(iso).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatMoney(amount: number): string {
  return `$${amount.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
}

function trialDaysRemaining(trialEnd?: string | null): number | null {
  if (!trialEnd) return null;
  const ms = new Date(trialEnd).getTime() - Date.now();
  return Math.max(0, Math.ceil(ms / 86_400_000));
}

/** Convert an <input type="date"> value (yyyy-mm-dd) to an end-of-day UTC ISO string. */
function dateInputToUtcIso(value: string): string {
  return new Date(`${value}T23:59:59Z`).toISOString();
}

/** Format a Date as a local-timezone yyyy-mm-dd for <input type="date"> min bounds. */
function toLocalDateInput(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

interface EditDiff {
  changes: string[];
  warnings: string[];
  hasChanges: boolean;
}

export default function Subscriptions() {
  const [rows, setRows] = useState<AdminTenantSubscription[]>([]);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [addons, setAddons] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [snackbar, setSnackbar] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);

  // Provision/edit drawer state (shared form; mode controls trial section and submit action)
  const [dialogMode, setDialogMode] = useState<'provision' | 'edit'>('provision');
  const [provisionTarget, setProvisionTarget] = useState<AdminTenantSubscription | null>(null);
  const [planId, setPlanId] = useState('');
  const [selectedAddonIds, setSelectedAddonIds] = useState<string[]>([]);
  const [addonQtys, setAddonQtys] = useState<Record<string, number>>({});
  const [addonPrices, setAddonPrices] = useState<Record<string, string>>({});
  const [trialMode, setTrialMode] = useState<'none' | 'days' | 'date'>('days');
  const [trialDays, setTrialDays] = useState(30);
  const [trialDate, setTrialDate] = useState('');
  const [provisioning, setProvisioning] = useState(false);

  // Trial dialog state
  const [trialTarget, setTrialTarget] = useState<AdminTenantSubscription | null>(null);
  const [trialAction, setTrialAction] = useState<'extend' | 'end'>('extend');
  const [newTrialDate, setNewTrialDate] = useState('');
  const [updatingTrial, setUpdatingTrial] = useState(false);

  // Per-row Chargebee sync state
  const [syncingTenantId, setSyncingTenantId] = useState<string | null>(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setFetchError(null);
      const [subs, tenantPlans, tenantAddons] = await Promise.all([
        billingService.getAdminTenantSubscriptions(),
        billingService.getAvailablePlans(),
        billingService.getAvailableTenantAddons(),
      ]);
      setRows(subs);
      setPlans(tenantPlans);
      setAddons(tenantAddons);
    } catch (error: unknown) {
      const apiError = getApiErrorInfo(error);
      setFetchError(apiError.message || 'Failed to load subscriptions');
    } finally {
      setLoading(false);
    }
  };

  const filteredRows = useMemo(
    () => rows.filter((r) => r.tenantName.toLowerCase().includes(searchTerm.toLowerCase())),
    [rows, searchTerm]
  );

  const applyUpdatedSubscription = (tenantId: string, subscription: Subscription) => {
    setRows((prev) => prev.map((r) => (r.tenantId === tenantId ? { ...r, subscription } : r)));
  };

  const openProvisionDialog = (row: AdminTenantSubscription) => {
    setDialogMode('provision');
    setProvisionTarget(row);
    setPlanId(plans[0]?.id ?? '');
    setSelectedAddonIds([]);
    setAddonQtys({});
    setAddonPrices({});
    setTrialMode('days');
    setTrialDays(30);
    setTrialDate('');
  };

  const openEditDialog = (row: AdminTenantSubscription) => {
    const sub = row.subscription;
    if (!sub) return;
    setDialogMode('edit');
    setProvisionTarget(row);
    setPlanId(sub.planId);
    setSelectedAddonIds(sub.activeAddonIds ?? []);
    setAddonQtys({ ...(sub.addonQuantities ?? {}) });
    // Prefill with current per-seat prices synced from Chargebee so negotiated
    // pricing stays explicit (a blank field falls back to the catalog price).
    setAddonPrices(
      Object.fromEntries(
        Object.entries(sub.addonUnitPrices ?? {}).map(([id, price]) => [id, price.toFixed(2)])
      )
    );
  };

  const toggleAddon = (addonId: string) => {
    setSelectedAddonIds((prev) =>
      prev.includes(addonId) ? prev.filter((id) => id !== addonId) : [...prev, addonId]
    );
  };

  const handleSubmitDialog = async () => {
    if (!provisionTarget || !planId) return;
    try {
      setProvisioning(true);
      const addonQuantities: Record<string, number> = {};
      const addonUnitPrices: Record<string, number> = {};
      for (const id of selectedAddonIds) {
        addonQuantities[id] = addonQtys[id] ?? 1;
        const priceInput = addonPrices[id]?.trim();
        if (priceInput) {
          const parsed = parseFloat(priceInput);
          if (Number.isNaN(parsed) || parsed < 0) {
            const addonName = addons.find((a) => a.id === id)?.name ?? id;
            setSnackbar({ message: `Invalid per-seat price for add-on "${addonName}".`, severity: 'error' });
            setProvisioning(false);
            return;
          }
          addonUnitPrices[id] = parsed;
        }
      }
      let subscription: Subscription;
      if (dialogMode === 'provision') {
        subscription = await billingService.adminProvisionSubscription(provisionTarget.tenantId, {
          planId,
          quantity: 1,
          ...(selectedAddonIds.length > 0 ? { addonIds: selectedAddonIds, addonQuantities } : {}),
          ...(Object.keys(addonUnitPrices).length > 0 ? { addonUnitPrices } : {}),
          ...(trialMode === 'days' && trialDays > 0 ? { trialDays } : {}),
          ...(trialMode === 'date' && trialDate ? { trialEndUtc: dateInputToUtcIso(trialDate) } : {}),
        });
      } else {
        // Edit sends the complete items list. Omitted add-ons are removed in Chargebee.
        subscription = await billingService.adminUpdateSubscription(provisionTarget.tenantId, {
          planId,
          quantity: 1,
          addonIds: selectedAddonIds,
          addonQuantities,
          ...(Object.keys(addonUnitPrices).length > 0 ? { addonUnitPrices } : {}),
        });
      }
      applyUpdatedSubscription(provisionTarget.tenantId, subscription);
      setSnackbar({
        message:
          dialogMode === 'provision'
            ? `Subscription provisioned for ${provisionTarget.tenantName} (${SUBSCRIPTION_STATUS_CONFIG[subscription.status]?.label ?? subscription.status})`
            : `Subscription updated for ${provisionTarget.tenantName}. The new pricing bills at the next renewal.`,
        severity: 'success',
      });
      setProvisionTarget(null);
    } catch (error: unknown) {
      const apiError = getApiErrorInfo(error);
      setSnackbar({
        message:
          apiError.message ||
          (dialogMode === 'provision' ? 'Failed to provision subscription' : 'Failed to update subscription'),
        severity: 'error',
      });
    } finally {
      setProvisioning(false);
    }
  };

  const openTrialDialog = (row: AdminTenantSubscription) => {
    setTrialTarget(row);
    setTrialAction('extend');
    const current = row.subscription?.trialEnd;
    setNewTrialDate(current ? current.substring(0, 10) : '');
  };

  const handleUpdateTrial = async () => {
    if (!trialTarget) return;
    try {
      setUpdatingTrial(true);
      const subscription = await billingService.adminUpdateTrial(trialTarget.tenantId, {
        ...(trialAction === 'extend' && newTrialDate ? { trialEndUtc: dateInputToUtcIso(newTrialDate) } : {}),
      });
      applyUpdatedSubscription(trialTarget.tenantId, subscription);
      setSnackbar({
        message:
          trialAction === 'end'
            ? `Trial ended: ${trialTarget.tenantName} is now ${SUBSCRIPTION_STATUS_CONFIG[subscription.status]?.label ?? subscription.status}`
            : `Trial for ${trialTarget.tenantName} now ends ${formatDate(subscription.trialEnd)}`,
        severity: 'success',
      });
      setTrialTarget(null);
    } catch (error: unknown) {
      const apiError = getApiErrorInfo(error);
      setSnackbar({ message: apiError.message || 'Failed to update trial', severity: 'error' });
    } finally {
      setUpdatingTrial(false);
    }
  };

  const handleSync = async (row: AdminTenantSubscription) => {
    const chargebeeId = row.subscription?.chargebeeSubscriptionId;
    if (!chargebeeId) return;
    try {
      setSyncingTenantId(row.tenantId);
      const subscription = await billingService.adminSyncSubscription(chargebeeId);
      applyUpdatedSubscription(row.tenantId, subscription);
      setSnackbar({
        message: `${row.tenantName} synced from Chargebee (${SUBSCRIPTION_STATUS_CONFIG[subscription.status]?.label ?? subscription.status})`,
        severity: 'success',
      });
    } catch (error: unknown) {
      const apiError = getApiErrorInfo(error);
      setSnackbar({ message: apiError.message || 'Failed to sync from Chargebee', severity: 'error' });
    } finally {
      setSyncingTenantId(null);
    }
  };

  const canProvision = (row: AdminTenantSubscription) => {
    const status = row.subscription?.status;
    return !status || !['active', 'in_trial', 'non_renewing', 'paused'].includes(status);
  };

  const selectedPlan = plans.find((p) => p.id === planId);
  // Add-ons are priced per seat; the base plan is a flat fee.
  const estimatedInvoiceTotal =
    (selectedPlan?.price ?? 0) +
    selectedAddonIds.reduce((sum, id) => {
      const addon = addons.find((a) => a.id === id);
      const override = parseFloat(addonPrices[id] ?? '');
      const unitPrice = !Number.isNaN(override) && override >= 0 ? override : addon?.price ?? 0;
      return sum + unitPrice * (addonQtys[id] ?? 1);
    }, 0);
  const minDateInput = toLocalDateInput(new Date(Date.now() + 86_400_000));

  // What will actually change if the edit form is saved, compared against the
  // synced subscription. Drives the change summary and gates the Save button so
  // a no-op save can never silently reset unsynced negotiated prices.
  const editDiff = useMemo<EditDiff>(() => {
    const sub = provisionTarget?.subscription;
    if (dialogMode !== 'edit' || !sub) return { changes: [], warnings: [], hasChanges: false };

    const changes: string[] = [];
    const warnings: string[] = [];

    if (planId && planId !== sub.planId) {
      const from = plans.find((p) => p.id === sub.planId)?.name ?? sub.planName ?? sub.planId;
      const to = plans.find((p) => p.id === planId)?.name ?? planId;
      changes.push(`Plan: ${from} to ${to}`);
    }

    const currentIds = sub.activeAddonIds ?? [];
    for (const addon of addons) {
      const name = addon.name || addon.id;
      const inCurrent = currentIds.includes(addon.id);
      const inNew = selectedAddonIds.includes(addon.id);
      const override = parseFloat(addonPrices[addon.id] ?? '');
      const newPrice = !Number.isNaN(override) && override >= 0 ? override : addon.price;
      const newQty = addonQtys[addon.id] ?? 1;

      if (!inCurrent && inNew) {
        changes.push(`Add ${name}: ${newQty} seat${newQty === 1 ? '' : 's'} at ${formatMoney(newPrice)}/seat`);
      } else if (inCurrent && !inNew) {
        changes.push(`Remove ${name} (access is revoked on save)`);
      } else if (inCurrent && inNew) {
        const currentQty = sub.addonQuantities?.[addon.id] ?? 1;
        if (newQty !== currentQty) {
          changes.push(`${name} seats: ${currentQty} to ${newQty}`);
        }
        const currentPrice = sub.addonUnitPrices?.[addon.id];
        if (currentPrice === undefined) {
          warnings.push(
            `${name}: the current negotiated price has not been synced from Chargebee. ` +
              `Saving applies ${formatMoney(newPrice)}/seat. Sync this tenant first if it has a negotiated rate.`
          );
          if (!Number.isNaN(override)) {
            changes.push(`${name} price: set to ${formatMoney(newPrice)}/seat`);
          }
        } else if (Math.abs(newPrice - currentPrice) > 0.004) {
          changes.push(`${name} price: ${formatMoney(currentPrice)} to ${formatMoney(newPrice)}/seat`);
        }
      }
    }

    return { changes, warnings, hasChanges: changes.length > 0 };
  }, [dialogMode, provisionTarget, planId, plans, addons, selectedAddonIds, addonQtys, addonPrices]);

  // Full first-invoice estimate for ending a trial: flat plan fee plus every
  // add-on's seats at its negotiated (or catalog) per-seat price. Tenant plans
  // are usually $0 base, so the add-ons ARE the invoice.
  const trialSub = trialTarget?.subscription;
  const trialEndEstimate = trialSub
    ? trialSub.planAmount +
      (trialSub.activeAddonIds ?? []).reduce((sum, id) => {
        const qty = trialSub.addonQuantities?.[id] ?? 1;
        const unit = trialSub.addonUnitPrices?.[id] ?? addons.find((a) => a.id === id)?.price ?? 0;
        return sum + qty * unit;
      }, 0)
    : 0;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, gap: 2, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          placeholder="Search tenants…"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ width: { xs: '100%', sm: 320 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {fetchError ? (
        <Alert
          severity="error"
          variant="outlined"
          action={
            <Button color="inherit" size="small" onClick={fetchData}>
              Retry
            </Button>
          }
        >
          <Typography variant="body2" fontWeight={500}>
            Couldn't load subscriptions
          </Typography>
          <Typography variant="body2">{fetchError}</Typography>
        </Alert>
      ) : (
      <TableContainer component={Paper} sx={{ border: 1, borderColor: 'divider' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Tenant</TableCell>
              <TableCell align="right">Users</TableCell>
              <TableCell>Plan</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Trial Ends</TableCell>
              <TableCell>Next Billing</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredRows.map((row) => {
              const sub = row.subscription;
              const statusCfg = sub ? SUBSCRIPTION_STATUS_CONFIG[sub.status] : undefined;
              const daysLeft = sub?.status === 'in_trial' ? trialDaysRemaining(sub.trialEnd) : null;
              const totalSeats = sub
                ? Object.values(sub.addonQuantities ?? {}).reduce((a, b) => a + b, 0)
                : 0;
              const overSeats = totalSeats > 0 && row.userCount > totalSeats;
              const syncing = syncingTenantId === row.tenantId;
              return (
                <TableRow key={row.tenantId} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {row.tenantName}
                    </Typography>
                    {!row.isActive && (
                      <Typography variant="caption" color="text.secondary">
                        Tenant inactive
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {row.userCount}
                    {totalSeats > 0 && (
                      <Typography
                        variant="caption"
                        sx={{ display: 'block', color: overSeats ? 'warning.dark' : 'text.secondary' }}
                      >
                        {overSeats ? `over ${totalSeats} seats` : `of ${totalSeats} seats`}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{sub ? sub.planName || sub.planId : '—'}</TableCell>
                  <TableCell>
                    {sub ? (
                      <Chip
                        label={statusCfg?.label ?? sub.status}
                        size="small"
                        sx={subscriptionChipSx(statusCfg?.tone ?? 'neutral')}
                      />
                    ) : (
                      <Chip
                        label={NO_SUBSCRIPTION_CONFIG.label}
                        size="small"
                        icon={NO_SUBSCRIPTION_CONFIG.icon}
                        sx={subscriptionChipSx(NO_SUBSCRIPTION_CONFIG.tone)}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    {sub?.status === 'in_trial' ? (
                      <>
                        {formatDate(sub.trialEnd)}
                        {daysLeft !== null && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {daysLeft === 0 ? 'Ends today' : `${daysLeft} day${daysLeft === 1 ? '' : 's'} left`}
                          </Typography>
                        )}
                      </>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  <TableCell>{formatDate(sub?.nextBillingAt)}</TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: 'inline-flex', gap: 1, alignItems: 'center' }}>
                      {canProvision(row) ? (
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<ProvisionIcon />}
                          onClick={() => openProvisionDialog(row)}
                        >
                          Provision
                        </Button>
                      ) : (
                        <>
                          {sub?.status === 'in_trial' && (
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<TrialIcon />}
                              onClick={() => openTrialDialog(row)}
                            >
                              Manage Trial
                            </Button>
                          )}
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<EditIcon />}
                            onClick={() => openEditDialog(row)}
                          >
                            Edit
                          </Button>
                        </>
                      )}
                      {sub?.chargebeeSubscriptionId && (
                        <Tooltip
                          title={`Sync from Chargebee (last synced ${formatDateTime(sub.updatedAt)})`}
                        >
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => handleSync(row)}
                              disabled={syncing}
                              aria-label={`Sync ${row.tenantName} from Chargebee`}
                            >
                              {syncing ? (
                                <CircularProgress size={16} />
                              ) : (
                                <SyncIcon fontSize="small" />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
            {filteredRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    {rows.length === 0
                      ? 'No tenants yet. Tenants appear here once they are created.'
                      : 'No tenants match your search'}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      )}

      {/* Provision / edit drawer */}
      <Drawer
        anchor="right"
        open={Boolean(provisionTarget)}
        onClose={() => !provisioning && setProvisionTarget(null)}
        PaperProps={{
          role: 'dialog',
          'aria-labelledby': 'subscription-drawer-title',
          sx: { width: { xs: '100%', sm: 480 } },
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 2,
              px: 3,
              py: 2,
              borderBottom: 1,
              borderColor: 'divider',
            }}
          >
            <Box>
              <Typography id="subscription-drawer-title" variant="h6">
                {dialogMode === 'provision' ? 'Provision Subscription' : 'Edit Subscription'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {provisionTarget?.tenantName}
              </Typography>
            </Box>
            <IconButton
              onClick={() => setProvisionTarget(null)}
              disabled={provisioning}
              aria-label="Close"
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 2.5, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <TextField
              select
              label="Plan"
              value={planId}
              onChange={(e) => setPlanId(e.target.value)}
              fullWidth
            >
              {plans.map((plan) => (
                <MenuItem key={plan.id} value={plan.id}>
                  {plan.name} · {plan.price === 0 ? 'Free' : formatMoney(plan.price)} / {plan.period}
                </MenuItem>
              ))}
            </TextField>

            {addons.length > 0 && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Add-ons
                </Typography>
                {addons.map((addon) => (
                  <Box
                    key={addon.id}
                    sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, minHeight: 48, pt: 0.5 }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={selectedAddonIds.includes(addon.id)}
                          onChange={() => toggleAddon(addon.id)}
                        />
                      }
                      label={
                        <Typography variant="body2">
                          {addon.name || addon.id}
                          {addon.price > 0 && (
                            <Typography component="span" variant="body2" color="text.secondary">
                              {' '}· {formatMoney(addon.price)} / seat / {addon.period}
                            </Typography>
                          )}
                        </Typography>
                      }
                      sx={{ flex: 1, mr: 0 }}
                    />
                    {selectedAddonIds.includes(addon.id) && (
                      <>
                        <TextField
                          size="small"
                          type="number"
                          label="Seats"
                          value={addonQtys[addon.id] ?? 1}
                          onChange={(e) =>
                            setAddonQtys((prev) => ({
                              ...prev,
                              [addon.id]: Math.max(1, parseInt(e.target.value, 10) || 1),
                            }))
                          }
                          inputProps={{ min: 1 }}
                          helperText=" "
                          sx={{ width: 90, flexShrink: 0 }}
                        />
                        <TextField
                          size="small"
                          type="number"
                          label="Price/Seat"
                          placeholder={addon.price.toFixed(2)}
                          value={addonPrices[addon.id] ?? ''}
                          onChange={(e) =>
                            setAddonPrices((prev) => ({
                              ...prev,
                              [addon.id]: e.target.value,
                            }))
                          }
                          helperText={`Blank = $${addon.price.toFixed(2)}`}
                          sx={{ width: 120, flexShrink: 0 }}
                          InputProps={{
                            inputProps: { min: 0, step: 0.01 },
                            startAdornment: <InputAdornment position="start">$</InputAdornment>,
                          }}
                        />
                      </>
                    )}
                  </Box>
                ))}
              </Box>
            )}

            {dialogMode === 'provision' && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Trial Period
              </Typography>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={trialMode}
                onChange={(_, v) => v && setTrialMode(v)}
              >
                <ToggleButton value="none">No trial</ToggleButton>
                <ToggleButton value="days">Days from now</ToggleButton>
                <ToggleButton value="date">Specific date</ToggleButton>
              </ToggleButtonGroup>

              {trialMode === 'days' && (
                <Box sx={{ mt: 2 }}>
                  <TextField
                    label="Trial days"
                    type="number"
                    value={trialDays}
                    onChange={(e) => setTrialDays(Math.max(1, parseInt(e.target.value, 10) || 1))}
                    inputProps={{ min: 1, max: 365 }}
                    sx={{ width: 140 }}
                  />
                </Box>
              )}
              {trialMode === 'date' && (
                <Box sx={{ mt: 2 }}>
                  <TextField
                    label="Trial ends"
                    type="date"
                    value={trialDate}
                    onChange={(e) => setTrialDate(e.target.value)}
                    inputProps={{ min: minDateInput }}
                    InputLabelProps={{ shrink: true }}
                    helperText="Ends end of day UTC"
                    sx={{ width: 200 }}
                  />
                </Box>
              )}
            </Box>
            )}

            {dialogMode === 'edit' && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Changes
                </Typography>
                {editDiff.changes.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No changes yet. Adjust the plan, add-ons, seats, or prices above.
                  </Typography>
                ) : (
                  <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                    {editDiff.changes.map((change) => (
                      <Typography key={change} component="li" variant="body2" sx={{ mb: 0.5 }}>
                        {change}
                      </Typography>
                    ))}
                  </Box>
                )}
                {editDiff.warnings.map((warning) => (
                  <Alert key={warning} severity="warning" variant="outlined" sx={{ mt: 1 }}>
                    {warning}
                  </Alert>
                ))}
              </Box>
            )}

            <Alert severity="info" variant="outlined">
              {dialogMode === 'edit'
                ? `The current paid term is unchanged; the next renewal invoice bills the new configuration${estimatedInvoiceTotal > 0 ? ` (est. ${formatMoney(estimatedInvoiceTotal)})` : ''}. Newly added add-ons are available immediately; removed add-ons are revoked immediately.`
                : trialMode === 'none'
                ? `The subscription activates immediately${estimatedInvoiceTotal > 0 ? ` and an invoice for ${formatMoney(estimatedInvoiceTotal)} is generated` : ''}. Chargebee does not charge a card automatically for provisioned subscriptions: send the invoice from Chargebee.`
                : 'The subscription starts in trial. If no payment method is added before the trial ends, Chargebee cancels the subscription and the tenant loses access. The trial can be extended or ended at any time while in trial, but an active subscription cannot be moved back to trial.'}
            </Alert>
          </Box>

          <Box
            sx={{
              px: 3,
              py: 2,
              borderTop: 1,
              borderColor: 'divider',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 1,
            }}
          >
            <Button onClick={() => setProvisionTarget(null)} disabled={provisioning}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmitDialog}
              disabled={
                provisioning ||
                !planId ||
                (dialogMode === 'provision' && trialMode === 'date' && !trialDate) ||
                (dialogMode === 'edit' && !editDiff.hasChanges)
              }
              startIcon={provisioning ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              {dialogMode === 'provision'
                ? provisioning ? 'Provisioning…' : 'Provision Subscription'
                : provisioning ? 'Saving…' : 'Save Changes'}
            </Button>
          </Box>
        </Box>
      </Drawer>

      {/* Trial management dialog */}
      <Dialog open={Boolean(trialTarget)} onClose={() => !updatingTrial && setTrialTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Manage Trial for {trialTarget?.tenantName}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Current trial ends {formatDate(trialTarget?.subscription?.trialEnd)}.
            </Typography>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={trialAction}
              onChange={(_, v) => v && setTrialAction(v)}
            >
              <ToggleButton value="extend">Change end date</ToggleButton>
              <ToggleButton value="end">End trial now</ToggleButton>
            </ToggleButtonGroup>

            {trialAction === 'extend' ? (
              <TextField
                label="New trial end"
                type="date"
                value={newTrialDate}
                onChange={(e) => setNewTrialDate(e.target.value)}
                inputProps={{ min: minDateInput }}
                InputLabelProps={{ shrink: true }}
                helperText="Ends end of day UTC"
                sx={{ width: 200 }}
              />
            ) : (
              <Alert severity="warning" variant="outlined">
                {`Ending the trial activates the subscription immediately and generates an invoice for the current term (est. ${formatMoney(trialEndEstimate)}${trialSub?.currency ? ` ${trialSub.currency}` : ''}). This cannot be undone.`}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTrialTarget(null)} disabled={updatingTrial}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color={trialAction === 'end' ? 'warning' : 'primary'}
            onClick={handleUpdateTrial}
            disabled={updatingTrial || (trialAction === 'extend' && !newTrialDate)}
            startIcon={updatingTrial ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {updatingTrial ? 'Saving…' : trialAction === 'end' ? 'End Trial Now' : 'Save Trial End'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(snackbar)}
        autoHideDuration={6000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar?.severity}
          variant="outlined"
          onClose={() => setSnackbar(null)}
          sx={{ bgcolor: 'background.paper' }}
        >
          {snackbar?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
